import { useState, useRef } from "react";
import axios, { API } from "../../api";
import { PhotoEditModal } from "./PhotoEditModal";

/**
 * Lets the user pick an existing photo from their phone (via native picker)
 * and send it to Aura. iOS Safari opens Photos chooser; Android opens Gallery.
 *
 * Two-step UX: pick photo → preview + optional context → send.
 */
export function PhotoPickerModal({ onClose, onCaptured, onMoodUpdate, initialContext = "", onPaintWallpaper, paintWallpaperLocked }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("idle"); // idle | review | sending | done
  const [preview, setPreview] = useState(null); // data URL
  const [pickedFile, setPickedFile] = useState(null); // original File (for paint-wallpaper handoff)
  const [context, setContext] = useState(initialContext);
  const [reaction, setReaction] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  // Immediately open the picker when the modal mounts
  const triggerPick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { onClose?.(); return; }
    if (!file.type.startsWith("image/")) {
      setError("That doesn't look like a photo. Please pick an image.");
      return;
    }
    setPickedFile(file);
    // Resize on canvas for cost/speed (max 1024px on longest edge)
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1024;
          const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          setPreview(dataUrl);
          setStep("review");
        };
        img.onerror = () => setError("Couldn't read that photo. Try another.");
        img.src = reader.result;
      } catch (err) {
        setError("Couldn't process that photo. Try another.");
      }
    };
    reader.onerror = () => setError("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  const send = async () => {
    if (!preview) return;
    setStep("sending");
    setError("");
    try {
      const base64 = preview.split(",")[1];
      const res = await axios.post(`${API}/vision/analyze`, {
        image_base64: base64,
        context: context.trim() || null,
      });
      setReaction(res.data.reaction || "");
      setStep("done");
      if (onMoodUpdate && res.data.mood) {
        onMoodUpdate({ mood: res.data.mood, emoji: res.data.mood_emoji });
      }
      if (onCaptured) {
        onCaptured({
          image_base64: base64,
          reaction: res.data.reaction,
          mood: res.data.mood,
          mood_emoji: res.data.mood_emoji,
          mood_shifted: res.data.mood_shifted,
          current_streak: res.data.current_streak,
          streak_grew: res.data.streak_grew,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong sharing that photo.");
      setStep("review");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="photo-picker-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="photo-picker-modal"
      >
        <div className="journal-header">
          <h2>🖼️ Show Aura a photo</h2>
          <button className="journal-close" onClick={onClose} data-testid="photo-picker-close">×</button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          style={{ display: "none" }}
          data-testid="photo-picker-file-input"
        />

        {step === "idle" && (
          <div className="photo-picker-idle">
            <p className="photo-picker-hint">
              Pick a photo from your library — Aura will look and react.
              Your photo only goes to Aura, it's not shared anywhere else.
            </p>
            <button className="init-btn" onClick={triggerPick} data-testid="photo-picker-pick-btn">
              Choose a photo
            </button>
            <button className="ghost-btn small" onClick={onClose} data-testid="photo-picker-cancel">
              Cancel
            </button>
          </div>
        )}

        {step === "review" && preview && (
          <div className="photo-picker-review">
            <img src={preview} alt="preview" className="photo-picker-preview" />
            <input
              type="text"
              className="init-input"
              placeholder="Add a note for Aura (optional)…"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              data-testid="photo-picker-context"
              maxLength={200}
            />
            {error && <div className="error-message">{error}</div>}
            <button
              className="ghost-btn small"
              onClick={() => setEditing(true)}
              data-testid="photo-picker-edit-btn"
            >
              ✏️ Edit photo
            </button>
            <button className="init-btn" onClick={send} data-testid="photo-picker-send">
              Show Aura
            </button>
            {onPaintWallpaper && (
              <button
                className="init-btn camera-paint-btn"
                onClick={() => {
                  if (paintWallpaperLocked) {
                    onPaintWallpaper({ locked: true });
                    return;
                  }
                  onPaintWallpaper({ file: pickedFile });
                }}
                data-testid="photo-picker-paint-wallpaper-btn"
                title={paintWallpaperLocked ? "Premium feature" : "Paint me into a wallpaper"}
              >
                🎨 Paint into wallpaper {paintWallpaperLocked && <span aria-hidden> 🔒</span>}
              </button>
            )}
            <button
              className="ghost-btn small"
              onClick={() => { setPreview(null); setStep("idle"); triggerPick(); }}
              data-testid="photo-picker-different"
            >
              Pick a different photo
            </button>
          </div>
        )}

        {step === "sending" && (
          <div className="photo-picker-sending">
            <p>✨ Aura is looking at your photo…</p>
          </div>
        )}

        {step === "done" && (
          <div className="photo-picker-done" data-testid="photo-picker-done">
            <img src={preview} alt="" className="photo-picker-preview small" />
            <p className="aura-reaction">{reaction}</p>
            <button className="init-btn" onClick={onClose} data-testid="photo-picker-done-btn">
              Done
            </button>
          </div>
        )}
      </div>
      {editing && preview && (
        <PhotoEditModal
          src={preview}
          onCancel={() => setEditing(false)}
          onApply={(editedB64) => {
            setPreview(`data:image/jpeg;base64,${editedB64}`);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}
